import React from 'react';

interface AboutProps {
  onClose: () => void;
}

export function About({ onClose }: AboutProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#e6e6e6] bg-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#d5a32a] rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1b2c5a] font-['Anton'] tracking-wide">About Career Pathways Portal</h1>
              <p className="text-sm text-[#707070] font-['Roboto']">Empowering career choices with local data that matters</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-[#d5a32a] text-[#ffffff] rounded-xl hover:bg-[#f2c94c] transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-lg hover:shadow-xl font-['Roboto']"
            title="Close Modal"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-[#707070] leading-relaxed font-['Roboto'] text-lg bg-[#f8f9fa] p-6 rounded-xl border border-[#e6e6e6]">
                The Forward Sumner Career Pathways Portal helps students, families, jobseekers, and educators explore careers and education options backed by local, trusted data. Search hundreds of careers, see real opportunities with area employers, and find financial aid that can make training affordable—all in one place.
              </p>
            </section>

            {/* What you can do here */}
            <section>
              <h2 className="text-2xl font-bold text-[#1b2c5a] mb-6 flex items-center gap-3 font-['Anton'] tracking-wide">
                <div className="w-8 h-8 bg-[#d5a32a] rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                What you can do here
              </h2>
              <div className="space-y-4 text-[#707070] leading-relaxed font-['Roboto']">
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p><strong className="text-[#1b2c5a]">Explore careers</strong> across sectors and high school CTE clusters, from Healthcare and Advanced Manufacturing to Construction, Education, Supply Chain, and more.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p><strong className="text-[#1b2c5a]">Compare pay, hiring activity, and growth</strong> for each role (annual salary range, hourly wage, current employment, and projected change).</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p><strong className="text-[#1b2c5a]">See who hires locally.</strong> Each career page lists local businesses connected to that occupation so you can discover employers, work-based learning ideas, and leads.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p><strong className="text-[#1b2c5a]">Understand the training.</strong> View the typical education or credential for entry and program options at nearby schools.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p><strong className="text-[#1b2c5a]">Check industry context.</strong> Explore which industries employ each occupation and where employment is growing.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p><strong className="text-[#1b2c5a]">Find money for school.</strong> Use the Financial Aid hub to see Tennessee Promise & Reconnect, HOPE & TSAA grants, Pell, Work Study, and local options (e.g., TCAT Nashville and Vol State Community College).</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p><strong className="text-[#1b2c5a]">Give feedback.</strong> Tell us what's working and what to improve using the Feedback link in the header and footer.</p>
                </div>
              </div>
            </section>

            {/* Who it's for */}
            <section>
              <h2 className="text-2xl font-bold text-[#1b2c5a] mb-6 flex items-center gap-3 font-['Anton'] tracking-wide">
                <div className="w-8 h-8 bg-[#d5a32a] rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                Who it's for
              </h2>
              <div className="grid md:grid-cols-2 gap-4 font-['Roboto']">
                <div className="bg-gradient-to-br from-[#f8f9fa] to-white p-6 rounded-xl border border-[#e6e6e6] shadow-sm">
                  <h3 className="text-[#1b2c5a] font-bold text-lg mb-2">Students & families</h3>
                  <p className="text-[#707070] leading-relaxed">Turn interests into concrete next steps—courses, programs, and local employers.</p>
                </div>
                <div className="bg-gradient-to-br from-[#f8f9fa] to-white p-6 rounded-xl border border-[#e6e6e6] shadow-sm">
                  <h3 className="text-[#1b2c5a] font-bold text-lg mb-2">Adult jobseekers</h3>
                  <p className="text-[#707070] leading-relaxed">Compare options, credentials, and wages to plan a career move.</p>
                </div>
                <div className="bg-gradient-to-br from-[#f8f9fa] to-white p-6 rounded-xl border border-[#e6e6e6] shadow-sm">
                  <h3 className="text-[#1b2c5a] font-bold text-lg mb-2">Educators & counselors</h3>
                  <p className="text-[#707070] leading-relaxed">Use clear, county level data to advise students and align programs.</p>
                </div>
                <div className="bg-gradient-to-br from-[#f8f9fa] to-white p-6 rounded-xl border border-[#e6e6e6] shadow-sm">
                  <h3 className="text-[#1b2c5a] font-bold text-lg mb-2">Employers & partners</h3>
                  <p className="text-[#707070] leading-relaxed">See the local talent landscape and connect to schools and training.</p>
                </div>
              </div>
            </section>

            {/* How the data works */}
            <section>
              <h2 className="text-2xl font-bold text-[#1b2c5a] mb-6 flex items-center gap-3 font-['Anton'] tracking-wide">
                <div className="w-8 h-8 bg-[#d5a32a] rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                How the data works (in plain language)
              </h2>
              <div className="bg-[#f8f9fa] p-6 rounded-xl border border-[#e6e6e6] space-y-4 font-['Roboto']">
                <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Employment & wages</strong> come from the U.S. Bureau of Labor Statistics (BLS) and other state/federal sources.</p>
                <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Job descriptions, skills, and tools</strong> are adapted from O*NET OnLine.</p>
                <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Job postings</strong> reflect unique online listings aggregated across sources for the current calendar year—they show hiring activity in our area, not guaranteed openings or hires.</p>
                <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Growth trends and job openings projections</strong> show near-term outlooks for Sumner County so you can gauge momentum.</p>
                <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Education requirements</strong> indicate the typical entry-level credential nationally for that occupation; local employers may ask for more or less.</p>
                <p className="text-[#707070] leading-relaxed">Numbers are estimates and can change as new information becomes available. Your experience (pay, title, requirements) can vary by employer, shift, and credentials.</p>
              </div>
            </section>

            {/* Key terms */}
            <section>
              <h2 className="text-2xl font-bold text-[#1b2c5a] mb-6 flex items-center gap-3 font-['Anton'] tracking-wide">
                <div className="w-8 h-8 bg-[#d5a32a] rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                </div>
                Key terms you'll see
              </h2>
              <div className="grid gap-4 font-['Roboto']">
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Annual Salary Range / Hourly Wage:</strong> Typical local pay estimates for the role.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Current Employment:</strong> Estimated number of people working in that job in Sumner County.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Growth Trend:</strong> Percent change over a recent multi-year window in our county.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Job Postings (Last 12 Months):</strong> Count of unique online postings for this role over the past 12 months.</p>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#e6e6e6] shadow-sm">
                  <p className="text-[#707070] leading-relaxed"><strong className="text-[#1b2c5a]">Required Education:</strong> Typical education or credential to start in the role.</p>
                </div>
              </div>
            </section>

            {/* Accessibility, privacy, and feedback */}
            <section>
              <h2 className="text-2xl font-bold text-[#1b2c5a] mb-6 flex items-center gap-3 font-['Anton'] tracking-wide">
                <div className="w-8 h-8 bg-[#d5a32a] rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#1b2c5a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                Accessibility, privacy, and feedback
              </h2>
              <div className="bg-gradient-to-r from-[#1b2c5a] to-[#162844] p-6 rounded-xl text-white">
                <p className="text-white leading-relaxed font-['Roboto']">
                  We're committed to a simple, mobile-friendly experience. If you need content in another format, find something that looks off, or have ideas to improve the Portal, please use <strong className="text-[#d5a32a]">Feedback</strong> or <strong className="text-[#d5a32a]">Contact</strong> in the footer.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
